import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog, ErrorState } from '@ui-kit';
import type { ModuleNode, ApiError } from '../types/showcase';
import { operationsForCode, type SafeGetOperation } from '../data/safe-get-operations';
import { apiClient } from '../api/showcaseApi';

/**
 * TryItOutPanel — queue #645 §UAT-2026-07-07-R645 §R645-4.
 *
 * publicApis 배지 클릭 시 열리는 "API 탐색(try-it-out)" 드로어. `SAFE_GET_OPERATIONS`
 * allowlist(§R645-3)에서 이 노드에 매핑된 operation 만 선택 가능 — 자유 URL 입력은 없다.
 * 선언된 쿼리 파라미터만 입력받아 기존 `apiClient`(Bearer 자동 첨부·ApiResponse 언랩·ApiError
 * 정규화)로 실호출하고 응답을 표시한다. 쓰기(POST/PUT/DELETE) 계열은 이 allowlist 자체에
 * 없으므로 이 컴포넌트에서 우발적으로 호출할 방법이 없다.
 *
 * ui-kit `FormDialog` 재사용 — role=dialog·Esc·포커스 트랩은 그대로, "호출" 버튼이
 * onSubmit 을 트리거해도 드로어는 닫히지 않고 응답을 아래에 표시한다(취소/Esc 로만 닫힘).
 */

interface Props {
  node: ModuleNode;
  open: boolean;
  onClose: () => void;
}

type CallState = 'idle' | 'loading' | 'empty' | 'success' | 'error';

interface CallSuccess {
  status: number;
  ms: number;
  data: unknown;
}

function buildInitialParams(op: SafeGetOperation): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of op.params) out[p.name] = '';
  return out;
}

export function TryItOutPanel({ node, open, onClose }: Props) {
  const { t } = useTranslation();
  const ops = operationsForCode(node.code);
  const [selectedKey, setSelectedKey] = useState<string>(ops[0]?.key ?? '');
  const selectedOp = ops.find((op) => op.key === selectedKey) ?? ops[0];
  const [params, setParams] = useState<Record<string, string>>(() => (selectedOp ? buildInitialParams(selectedOp) : {}));
  const [callState, setCallState] = useState<CallState>('idle');
  const [success, setSuccess] = useState<CallSuccess | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  if (!open || !selectedOp) return null;

  function handleSelectOp(key: string) {
    const op = ops.find((o) => o.key === key);
    setSelectedKey(key);
    setParams(op ? buildInitialParams(op) : {});
    setCallState('idle');
    setSuccess(null);
    setError(null);
  }

  async function handleCall() {
    const missingRequired = selectedOp!.params.some((p) => p.required && !params[p.name]?.trim());
    if (missingRequired) {
      setCallState('empty');
      return;
    }

    const queryParams: Record<string, string> = {};
    for (const p of selectedOp!.params) {
      const v = params[p.name]?.trim();
      if (v) queryParams[p.name] = v;
    }

    setCallState('loading');
    setError(null);
    const startedAt = performance.now();
    try {
      const res = await apiClient.get(selectedOp!.path, { params: queryParams });
      const ms = Math.round(performance.now() - startedAt);
      setSuccess({ status: res.status, ms, data: res.data });
      setCallState('success');
    } catch (e) {
      setError(e as ApiError);
      setCallState('error');
    }
  }

  function handleClose() {
    setCallState('idle');
    setSuccess(null);
    setError(null);
    onClose();
  }

  return (
    <FormDialog
      open={open}
      title={`${t('showcase.tryit.title')} — ${node.code}`}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={() => { void handleCall(); }}
      submitLabel={callState === 'loading' ? t('showcase.tryit.loading') : t('showcase.tryit.call')}
      submitTestId="showcase-tryit-call"
    >
      <p
        role="note"
        style={{
          margin: '0 0 var(--spacing-4) 0',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--spacing-2) var(--spacing-3)',
        }}
      >
        {t('showcase.tryit.banner')}
      </p>

      {ops.length > 1 && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label
            htmlFor="tryit-op-select"
            style={{ display: 'block', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}
          >
            {t('showcase.tryit.selectOp')}
          </label>
          <select
            id="tryit-op-select"
            value={selectedKey}
            onChange={(e) => handleSelectOp(e.target.value)}
            style={{ width: '100%', padding: 'var(--spacing-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-surface-border)' }}
          >
            {ops.map((op) => (
              <option key={op.key} value={op.key}>
                GET {op.path}
              </option>
            ))}
          </select>
        </div>
      )}
      {ops.length === 1 && (
        <p style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-secondary)' }}>
          GET {selectedOp.path}
        </p>
      )}

      {selectedOp.params.length > 0 && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>
            {t('showcase.tryit.param')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            {selectedOp.params.map((p) => (
              <label key={p.name} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)' }}>
                <span>
                  {p.name}
                  {p.required && <span style={{ color: 'var(--color-danger)' }}> *</span>}
                </span>
                <input
                  type="text"
                  value={params[p.name] ?? ''}
                  onChange={(e) => setParams((prev) => ({ ...prev, [p.name]: e.target.value }))}
                  style={{ padding: 'var(--spacing-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-surface-border)' }}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 응답 5상태 뷰 */}
      {callState === 'idle' && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
          {t('showcase.tryit.empty')}
        </p>
      )}
      {callState === 'empty' && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warning)' }}>
          {t('showcase.tryit.empty')}
        </p>
      )}
      {callState === 'loading' && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {t('showcase.tryit.loading')}
        </p>
      )}
      {callState === 'error' && error && (
        <ErrorState
          title={t('showcase.tryit.error')}
          message={`${error.message}${error.traceId ? ` (traceId: ${error.traceId})` : ''}`}
        />
      )}
      {callState === 'success' && success && (
        <div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
            {t('showcase.tryit.responseStatus', { status: success.status, ms: success.ms })}
          </p>
          <pre
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--spacing-3)',
              maxHeight: '240px',
              overflow: 'auto',
              margin: 0,
            }}
          >
            {JSON.stringify(success.data, null, 2)}
          </pre>
        </div>
      )}
    </FormDialog>
  );
}
